import { CommonModule } from '@angular/common';
import { Component, computed, HostBinding, input, output, ViewEncapsulation } from '@angular/core';
import {
    ArrowsValues,
    ArrowStyle,
    ArrowEvent,
    TextureTransitionType,
} from '../../models/floori-arrows.model';
import { PopupDirection } from '../../models/popup-direction';
import { FlooriButtonComponent } from '../floori-button/floori-button.component';
import { FlooriIconComponent } from '../floori-icon/floori-icon.component';
import { TooltipDirective } from '../../directives/tooltip/tooltip.directive';
import { fromEvent, merge, interval, take, takeUntil, map, tap, switchMap, startWith } from 'rxjs';

@Component({
    selector: 'floori-arrows',
    standalone: true,
    imports: [CommonModule, FlooriIconComponent, FlooriButtonComponent, TooltipDirective],
    templateUrl: './floori-arrows.component.html',
    styleUrl: './floori-arrows.component.scss',
    encapsulation: ViewEncapsulation.None
})
export class FlooriArrowsComponent {
    values = input<ArrowsValues>({ x: 0, y: 0 });
    step = input<number>(1);
    holdInterval = input<number>(150);
    showTooltips = input<boolean>(true);
    showMiddleIcon = input<boolean>(false);
    middleButtonIcon = input<string>('fl_pattern_o');
    arrowStyle = input<ArrowStyle>(ArrowStyle.studio);
    disabled = input<boolean>(false);

    click = output<ArrowEvent>();
    hold = output<ArrowEvent>();

    readonly popupDirection = PopupDirection;
    readonly transitionType = TextureTransitionType;

    readonly isMosaicsStyle = computed(() => this.arrowStyle() === ArrowStyle.mosaics);
    readonly buttonClass = computed(() =>
        this.isMosaicsStyle() ? 'mosaics-button' : 'floori-icon-button outline',
    );
    readonly iconName = computed(() =>
        this.isMosaicsStyle() ? 'fl_chevron_down_o' : 'fl_arrow_up_o',
    );
    readonly iconRotation = computed(() => (this.isMosaicsStyle() ? 'rotate-180' : ''));

    @HostBinding('class.floori-arrows') hostClass = true;

    handleButtonHold(value: number, type: TextureTransitionType, event: MouseEvent): void {
        if (this.disabled()) return;

        const button = event.target as HTMLElement;
        const mouseup$ = fromEvent(button, 'mouseup');
        const mouseleave$ = fromEvent(button, 'mouseleave');
        const blur$ = fromEvent(window, 'blur');
        const stop$ = merge(mouseup$, mouseleave$, blur$);
        const hold$ = interval(200).pipe(
            take(1),
            takeUntil(stop$),
            map(() => true),
        );

        merge(
            mouseup$.pipe(
                take(1),
                takeUntil(hold$),
                tap(() => this.handleButtonClick(value, type)),
            ),
            hold$.pipe(
                switchMap(() =>
                    interval(this.holdInterval()).pipe(
                        startWith(0),
                        tap(() => this.hold.emit({ value, type })),
                        takeUntil(stop$),
                    ),
                ),
            ),
        ).subscribe();
    }

    handleButtonClick(value: number, type: TextureTransitionType): void {
        if (this.disabled()) return;
        this.click.emit({ value, type });
    }
}
